import React from "react";
import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: theme.spacing(1),
  },
  content: {
    "& ul": {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(2.5),
    },
    "& li": {
      marginBottom: theme.spacing(0.5),
      fontSize: theme.typography.body2.fontSize,
      color: theme.palette.text.primary,
      lineHeight: 1.6,
    },
  },
  sectionHeading: {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  subHeading: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  lead: {
    lineHeight: 1.7,
    marginBottom: theme.spacing(1.5),
  },
  body: {
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    marginBottom: theme.spacing(1),
  },
  formula: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: "0.9rem",
    fontWeight: 500,
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.shape.borderRadius,
    display: "inline-block",
    margin: theme.spacing(0.5, 0),
    letterSpacing: "0.02em",
  },
  exampleBox: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5, 2),
    margin: theme.spacing(1, 0, 1.5),
  },
  summaryBox: {
    backgroundColor: "rgba(0, 146, 135, 0.04)",
    border: `1px solid rgba(0, 146, 135, 0.15)`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5, 2),
    margin: theme.spacing(1.5, 0, 0.5),
    "& ul": {
      marginBottom: theme.spacing(0.5),
    },
  },
  table: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    "& th": {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[50],
    },
  },
}));

const GrowingDegreeHoursInfo = () => {
  const classes = useStyles();
  return (
    <>
      <Typography variant="body1" className={classes.lead}>
        Growing Degree Hours are a way to measure heat accumulation that a plant
        uses for development. Instead of looking at average daily temperatures
        (like Growing Degree Days), GDH measures each hour of temperature,
        giving a more precise model for crops where development responds strongly
        to hourly temperature changes.
      </Typography>

      <Typography variant="h6" className={classes.sectionHeading}>
        The Concept
      </Typography>
      <ul>
        <li>Plants grow only when temperatures are above a base threshold.</li>
        <li>
          Growth increases as temperature increases up to an optimum or upper
          limit.
        </li>
        <li>Heat above the upper limit no longer contributes to growth.</li>
      </ul>
      <Typography variant="body2" className={classes.body}>
        GDH is widely used in fruit crops (e.g., berries, stone fruit) because
        it predicts flowering, fruit development, and ripening more accurately
        than daily averages.
      </Typography>

      <Typography variant="h6" className={classes.sectionHeading}>
        The Three Temperature Zones
      </Typography>

      <Typography variant="subtitle1" className={classes.subHeading}>
        <strong>A. Below the Base (T &lt; 4.5 °C)</strong>
      </Typography>
      <Typography variant="body2" className={classes.body}>
        No development happens below the base temperature.
      </Typography>
      <span className={classes.formula}>GDH = 0</span>

      <Typography variant="subtitle1" className={classes.subHeading}>
        <strong>B. Within the Growth Range (4.5 °C ≤ T ≤ 32 °C)</strong>
      </Typography>
      <Typography variant="body2" className={classes.body}>
        Growth is proportional to the temperature above the base:
      </Typography>
      <span className={classes.formula}>GDH = T − 4.5</span>

      <Box className={classes.exampleBox}>
        <Typography variant="subtitle2" gutterBottom>
          Example: One Hour at 20 °C
        </Typography>
        <Typography variant="body2" className={classes.body}>
          20 °C is between 4.5 °C and 32 °C, so:
        </Typography>
        <span className={classes.formula}>GDH = 20 − 4.5 = 15.5</span>
        <Typography
          variant="body2"
          className={classes.body}
          style={{ marginTop: 8, marginBottom: 0 }}
        >
          One hour at 20 °C contributes <strong>15.5</strong> Growing Degree
          Hours.
        </Typography>
      </Box>
    </>
  );
};

const WinterChillInfo = () => {
  const classes = useStyles();
  return (
    <>
      <Typography variant="body1" className={classes.lead}>
        Many fruiting plants — such as raspberries, blueberries, apples,
        cherries, and other temperate species — enter winter dormancy. To resume
        normal spring growth (budbreak, flowering, and fruiting), they must
        experience a certain amount of cold exposure, known as{" "}
        <strong>winter chill</strong>.
      </Typography>

      <Typography variant="h6" className={classes.sectionHeading}>
        Why Winter Chill Is Essential
      </Typography>
      <ul>
        <li>It resets the plant's physiology after the growing season</li>
        <li>It allows buds to develop properly</li>
        <li>It synchronises flowering and improves fruit set</li>
      </ul>

      <Typography variant="h6" className={classes.sectionHeading}>
        Insufficient Chill Symptoms
      </Typography>
      <Typography variant="body2" className={classes.body}>
        If a plant does not receive enough winter chill, it may show:
      </Typography>
      <ul>
        <li>Delayed and uneven budbreak</li>
        <li>Weak flowering</li>
        <li>Lower yields</li>
        <li>Poor fruit quality</li>
      </ul>

      <Typography variant="h6" className={classes.sectionHeading}>
        The 7.5 °C Chill Model
      </Typography>
      <Box className={classes.exampleBox}>
        <ul style={{ marginBottom: 0 }}>
          <li>
            Every hour with a temperature <strong>≤ 7.5 °C</strong> counts as{" "}
            <strong>one</strong> chill unit.
          </li>
          <li>
            Every hour above 7.5 °C counts as <strong>zero</strong> chill units.
          </li>
        </ul>
      </Box>
    </>
  );
};

const VPDInfo = () => {
  const classes = useStyles();
  return (
    <>
      <Typography variant="body1" className={classes.lead}>
        VPD measures the <strong>drying power of the air</strong>. It tells us
        how strongly the air pulls water from plant leaves. VPD is defined as
        the difference between the saturation vapour pressure (how much moisture
        the air could hold) and the actual vapour pressure (how much moisture
        the air currently contains).
      </Typography>

      <Typography variant="body2" className={classes.body}>
        Warm air can hold more moisture than cold air. At high temperature,
        saturation vapour pressure is high, so VPD tends to rise unless humidity
        also rises. At low humidity, actual vapour pressure is low → high VPD.
        At high humidity, actual vapour pressure is close to saturation → low
        VPD.
      </Typography>

      <Typography variant="h6" className={classes.sectionHeading}>
        How VPD Affects Plant Water Demand
      </Typography>

      <Typography variant="subtitle1" className={classes.subHeading}>
        <strong>Low VPD</strong>{" "}
        <Typography variant="body2" component="span">
          (humid air)
        </Typography>
      </Typography>
      <ul>
        <li>Stomata remain more open</li>
        <li>Transpiration is low; water loss is minimal</li>
        <li>
          Plants may become prone to fungal disease due to reduced
          airflow/moisture removal
        </li>
      </ul>

      <Typography variant="subtitle1" className={classes.subHeading}>
        <strong>Moderate VPD</strong>{" "}
        <Typography variant="body2" component="span">
          (ideal zone)
        </Typography>
      </Typography>
      <ul>
        <li>Stomata open naturally</li>
        <li>Transpiration is balanced</li>
        <li>Nutrient transport is efficient; growth is optimal</li>
      </ul>

      <Typography variant="subtitle1" className={classes.subHeading}>
        <strong>High VPD</strong>{" "}
        <Typography variant="body2" component="span">
          (dry air)
        </Typography>
      </Typography>
      <ul>
        <li>Air pulls water strongly from leaves</li>
        <li>Transpiration becomes very high</li>
        <li>
          Plants risk wilting, stomatal closure, reduced photosynthesis, and
          calcium-related disorders (tip burn, fruit cracking)
        </li>
      </ul>

      <Box className={classes.exampleBox}>
        <Typography variant="body2" style={{ marginBottom: 0 }}>
          Think of VPD as the <em>thirst of the air</em>: high VPD = air is
          very thirsty = plants lose more water; low VPD = air is not thirsty =
          plants lose less water.
        </Typography>
      </Box>

      <Typography variant="h6" className={classes.sectionHeading}>
        Why VPD Is Better Than Humidity Alone
      </Typography>
      <Typography variant="body2" className={classes.body}>
        Relative humidity alone can be misleading because humidity values are
        relative to temperature. VPD gives a physiologically meaningful measure
        because plants respond to the actual water vapour gradient between the
        leaf interior and the air — not just humidity percentage.
      </Typography>
      <Box className={classes.exampleBox}>
        <Typography variant="body2" component="div">
          <strong>70% RH at 30 °C</strong> → relatively high VPD
          <br />
          <strong>70% RH at 10 °C</strong> → much lower VPD
          <br />
          <Typography
            variant="body2"
            style={{ marginTop: 4 }}
          >
            Same relative humidity — but very different plant stress levels.
          </Typography>
        </Typography>
      </Box>

      <Box className={classes.summaryBox}>
        <Typography variant="subtitle2" gutterBottom>
          Summary
        </Typography>
        <ul>
          <li>
            VPD describes the drying power of the air, calculated from
            temperature and humidity.
          </li>
          <li>
            Higher VPD = higher plant water demand (more transpiration, more
            stress).
          </li>
          <li>
            Lower VPD = less water demand but can hinder growth if too low.
          </li>
          <li>
            Managing VPD helps optimise stomatal behaviour, nutrient uptake,
            plant growth rates, fruit quality, and water use efficiency.
          </li>
        </ul>
      </Box>
    </>
  );
};

const DeltaTInfo = () => {
  const classes = useStyles();
  return (
    <>
      <Typography variant="body1" className={classes.lead}>
        Delta-T is a spray application weather indicator used to judge whether
        environmental conditions are suitable for spraying pesticides. It is the
        difference between the <strong>air temperature</strong> (dry-bulb) and
        the <strong>wet-bulb temperature</strong>.
      </Typography>

      <Typography variant="h6" className={classes.sectionHeading}>
        What Delta-T Represents
      </Typography>
      <ul>
        <li>
          <strong>Evaporation rate</strong> of spray droplets — a high ΔT means
          very fast evaporation.
        </li>
        <li>
          <strong>Likelihood of spray drift</strong> — evaporating droplets
          become smaller and more likely to drift off-target.
        </li>
        <li>
          <strong>Risk of poor pesticide uptake</strong> — droplets that dry too
          quickly may leave crystals on the leaf, reducing absorption.
        </li>
        <li>
          <strong>Suitability of spraying conditions</strong> — helps operators
          decide when NOT to spray.
        </li>
      </ul>

      <Typography variant="body2" className={classes.body}>
        Delta-T is a combined humidity–temperature indicator, more useful than
        humidity or temperature alone.
      </Typography>

      <TableContainer component={Paper} variant="outlined" className={classes.table}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Delta-T Range</TableCell>
              <TableCell>Conditions</TableCell>
              <TableCell>Recommendation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <strong>&lt; 2</strong>
              </TableCell>
              <TableCell>Too humid / slow drying</TableCell>
              <TableCell>Risk of runoff → Usually avoid spraying</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>2 to 8</strong>
              </TableCell>
              <TableCell>Ideal droplet drying time</TableCell>
              <TableCell>Optimal for spraying</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>&gt; 8</strong>
              </TableCell>
              <TableCell>Very dry / rapid evaporation</TableCell>
              <TableCell>
                High drift + poor uptake → Avoid spraying
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" className={classes.sectionHeading}>
        Why Delta-T Matters
      </Typography>
      <ul>
        <li>
          <strong>Droplet evaporation rate:</strong> High ΔT → droplets dry too
          fast → less leaf absorption → reduced effectiveness.
        </li>
        <li>
          <strong>Spray drift risk:</strong> Evaporation shrinks droplets
          mid-air → smaller droplets travel farther → off-target contamination.
        </li>
        <li>
          <strong>Leaf wetness and uptake:</strong> Systemic pesticides require
          wet time to enter the leaf. High ΔT shortens wet time → lower
          efficacy.
        </li>
        <li>
          <strong>Environmental safety:</strong> High ΔT combined with wind
          increases chemical loss, environmental contamination, and resistance
          risk.
        </li>
        <li>
          <strong>Economical efficiency:</strong> Poor spray conditions = wasted
          chemical, fuel, and labour.
        </li>
      </ul>

      <Box className={classes.summaryBox}>
        <Typography variant="subtitle2" gutterBottom>
          Summary
        </Typography>
        <ul>
          <li>
            Delta-T is the difference between air temp and wet-bulb temp.
          </li>
          <li>It indicates how quickly spray droplets will evaporate.</li>
          <li>
            Ideal spraying occurs when ΔT is between <strong>2</strong> and{" "}
            <strong>8</strong>.
          </li>
          <li>
            High ΔT (&gt;8) → rapid evaporation, drift, and poor pesticide
            efficacy.
          </li>
          <li>
            Low ΔT (&lt;2) → droplets linger too long, increasing runoff and
            disease risk.
          </li>
        </ul>
        <Typography
          variant="body2"
          style={{ marginBottom: 0 }}
        >
          Delta-T is one of the most important weather-based decision tools for
          any grower wanting effective, safe, and compliant pesticide
          application.
        </Typography>
      </Box>
    </>
  );
};

const chartInfoContent: Record<
  string,
  { title: string; component: React.ReactNode }
> = {
  growing_hours: {
    title: "Growing Degree Hours (GDH)",
    component: <GrowingDegreeHoursInfo />,
  },
  chilling_hours: {
    title: "Winter Chill",
    component: <WinterChillInfo />,
  },
  vpd: {
    title: "Vapour Pressure Deficit (VPD)",
    component: <VPDInfo />,
  },
  delta_t: {
    title: "Delta-T Spray Advisory",
    component: <DeltaTInfo />,
  },
};

export const hasChartInfo = (chartId: string): boolean =>
  chartId in chartInfoContent;

const ChartInfoModal: React.FC<{
  chartId: string;
  open: boolean;
  onClose: () => void;
}> = ({ chartId, open, onClose }) => {
  const classes = useStyles();
  const info = chartInfoContent[chartId];

  if (!info) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h5">{info.title}</Typography>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        {info.component}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartInfoModal;
